import { useCallback, useEffect, useRef, useState } from 'react';

import { captureException } from '@sentry/react';
import { v4 } from 'uuid';

import { useFileUpload } from '@modules/_shared/utils/files/file-upload';

export type GalleryUploadingImage =
  | { id: string; tempUrl: string; state: 'uploading' }
  | { id: string; tempUrl: string; state: 'error'; error: Error };

export const useGalleryUploads = ({
  onFilesUploaded,
}: {
  onFilesUploaded?: (images: { id: string; url: string }[]) => Promise<unknown>;
}) => {
  const [uploadFile] = useFileUpload('image');
  // Discriminated so the error state cannot exist without its cause. With `error`
  // optional and independent of `state`, a future writer could set 'error' with nothing
  // to show, and `ImageUploadingCard` renders its whole overlay behind `{error && …}` —
  // a card with no icon and no label, which is the silent failure this component exists
  // to remove.
  const [uploadingImages, setUploadingImages] = useState<
    GalleryUploadingImage[]
  >([]);

  // Error cards outlive their upload, so handleFilesDrop does not release all object
  // URLs. Release whatever is left.
  const uploadingImagesRef = useRef(uploadingImages);
  useEffect(() => {
    uploadingImagesRef.current = uploadingImages;
  }, [uploadingImages]);
  useEffect(
    () => () => {
      uploadingImagesRef.current.forEach(img => {
        if (img.tempUrl) {
          URL.revokeObjectURL(img.tempUrl);
        }
      });
    },
    [],
  );

  const handleFilesDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !onFilesUploaded) {
        return;
      }

      const filesWithId = files.map(file => ({ id: v4(), file }));
      const tempUrls = filesWithId.map(({ id, file }) => {
        const tempUrl = URL.createObjectURL(file);
        return {
          id,
          tempUrl,
          state: 'uploading' as const,
        };
      });

      // An error card is the user's receipt for that failure; retrying is the
      // acknowledgement.
      setUploadingImages(prev => {
        prev.forEach(img => {
          if (img.state === 'error' && img.tempUrl) {
            URL.revokeObjectURL(img.tempUrl);
          }
        });
        return [...prev.filter(img => img.state !== 'error'), ...tempUrls];
      });

      const uploadResults = await Promise.allSettled(
        filesWithId.map(({ id, file }) => {
          return new Promise<{ url: string; id: string }>((resolve, reject) => {
            uploadFile(file, ({ data, error }) => {
              if (data) {
                resolve({ url: data.url, id });
              } else {
                reject(error);
              }
            });
          });
        }),
      );

      const uploadedFiles = uploadResults
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);

      const successfulIds = new Set(uploadedFiles.map(f => f.id));
      // Keep the rejection reason: showing the user a card is only half the fix if
      // the team still never learns an upload path is failing.
      const failures = new Map<string, Error>();

      uploadResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const error =
            result.reason instanceof Error
              ? result.reason
              : new Error(String(result.reason));
          failures.set(filesWithId[index].id, error);
          // No file name: agents name photos after clients and addresses.
          captureException(error, {
            tags: { feature: 'image-gallery-upload' },
            extra: {
              fileType: filesWithId[index].file.type,
              fileSize: filesWithId[index].file.size,
            },
          });
        }
      });

      setUploadingImages(prev => {
        const toCleanup: string[] = [];
        const next = prev.flatMap(img => {
          if (successfulIds.has(img.id)) {
            if (img.tempUrl) {
              toCleanup.push(img.tempUrl);
            }
            return [];
          }

          const failure = failures.get(img.id);
          if (failure != null) {
            // Keep tempUrl alive: the thumbnail shows behind the error overlay.
            return [{ ...img, state: 'error' as const, error: failure }];
          }

          return [img];
        });

        toCleanup.forEach(url => {
          URL.revokeObjectURL(url);
        });

        return next;
      });

      await onFilesUploaded(uploadedFiles);
    },
    [onFilesUploaded, uploadFile],
  );

  return { uploadingImages, handleFilesDrop };
};
